import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { getRuntimeConfig, getUiLanguage } from '@/shared/config';
import { logger } from '@/shared/lib';
import {
  ApiError,
  ApiErrorCode,
  codeFromStatus,
  errorEnvelopeSchema,
  parseRetryAfter,
} from './apiError';
import { serializeParams } from './params';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Не пытаться обновить токен при 401 (вход, обновление, выход). */
    skipAuthRefresh?: boolean;
    /** Запрос уже повторён после обновления токена. */
    authRetried?: boolean;
    /**
     * Запрос на адрес, выданный сервером вне API (presigned URL хранилища, COM-13): уходит тем же
     * инстансом (таймаут, нормализация ошибок), но без baseURL, без Authorization, Accept-Language
     * и X-Request-Id и без обновления токена — только заголовки, переданные явно (из ответа API).
     * Токен админки не должен уходить в объектное хранилище, а лишние заголовки ломают подпись URL.
     */
    external?: boolean;
  }
}

/**
 * Связь HTTP-слоя с сессией. shared не знает о сущности session: обработчики
 * подключает слой app (src/app/providers/sessionBridge.ts).
 */
export interface HttpAuthHandlers {
  /** Текущий access-токен (хранится только в памяти). */
  getAccessToken: () => string | null;
  /** Обновить access-токен по refresh-токену; null — обновить невозможно. */
  refreshAccessToken: () => Promise<string | null>;
  /** Сессия потеряна: выход и переход на экран входа с сохранением адреса. */
  onAuthFailure: () => void;
}

let authHandlers: HttpAuthHandlers | null = null;
let pendingRefresh: Promise<string | null> | null = null;

export function configureHttpAuth(handlers: HttpAuthHandlers | null): void {
  authHandlers = handlers;
  pendingRefresh = null;
}

/** Единственный axios-инстанс приложения (api-and-network.md). */
export const http = axios.create({
  baseURL: getRuntimeConfig().apiBaseUrl,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
  paramsSerializer: { serialize: (params) => serializeParams(params) },
});

export function createRequestId(): string {
  return globalThis.crypto.randomUUID().replaceAll('-', '');
}

function attachHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (config.external) {
    // Адрес — как выдал сервер (абсолютный или от корня сайта), без baseURL API.
    config.baseURL = '';
    return config;
  }
  config.headers.set('Accept-Language', getUiLanguage());
  if (!config.headers.has('X-Request-Id')) config.headers.set('X-Request-Id', createRequestId());
  const token = authHandlers?.getAccessToken();
  if (token && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
}

/** Одно обновление токена на все параллельные 401 (single-flight). */
function refreshOnce(handlers: HttpAuthHandlers): Promise<string | null> {
  if (!pendingRefresh) {
    pendingRefresh = handlers
      .refreshAccessToken()
      .catch((error: unknown) => {
        logger.warn('auth: refresh failed', { error: String(error) });
        return null;
      })
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

function headerValue(response: AxiosResponse | undefined, name: string): string | null {
  const value: unknown = response?.headers[name.toLowerCase()];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Нормализация любой ошибки axios в ApiError по конверту ТЗ 3.1.3. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (axios.isCancel(error)) {
    return new ApiError({ code: ApiErrorCode.Aborted, message: 'aborted', status: 0 });
  }
  if (!axios.isAxiosError(error)) {
    return new ApiError({
      code: ApiErrorCode.UnexpectedResponse,
      message: error instanceof Error ? error.message : String(error),
      status: 0,
    });
  }
  const sentRequestId = error.config?.headers?.get('X-Request-Id');
  const fallbackRequestId = typeof sentRequestId === 'string' ? sentRequestId : null;
  const response = error.response;
  if (!response) {
    return new ApiError({
      code: ApiErrorCode.NetworkError,
      message: error.message,
      status: 0,
      requestId: fallbackRequestId,
    });
  }
  const envelope = errorEnvelopeSchema.safeParse(response.data);
  const traceId = envelope.success ? (envelope.data.error.trace_id ?? null) : null;
  const requestId = headerValue(response, 'X-Request-Id') ?? traceId ?? fallbackRequestId;
  const retryAfterSec = parseRetryAfter(headerValue(response, 'Retry-After'));
  if (envelope.success) {
    return new ApiError({
      code: envelope.data.error.code,
      message: envelope.data.error.message,
      details: envelope.data.error.details,
      status: response.status,
      requestId,
      retryAfterSec,
    });
  }
  return new ApiError({
    code: codeFromStatus(response.status),
    message: `HTTP ${response.status}`,
    status: response.status,
    requestId,
    retryAfterSec,
  });
}

async function handleResponseError(error: unknown): Promise<AxiosResponse> {
  if (axios.isAxiosError(error)) {
    const handled = await retryAfterRefresh(error);
    if (handled) return handled;
  }
  const apiError = toApiError(error);
  if (apiError.code !== ApiErrorCode.Aborted && (apiError.status >= 500 || apiError.status === 0)) {
    logger.error('api: request failed', {
      code: apiError.code,
      status: apiError.status,
      requestId: apiError.requestId,
    });
  }
  throw apiError;
}

/** 401 → одна попытка обновить токен и повторить запрос; при неудаче — выход. */
async function retryAfterRefresh(error: AxiosError): Promise<AxiosResponse | null> {
  const config = error.config;
  const handlers = authHandlers;
  if (
    error.response?.status !== 401 ||
    !config ||
    config.skipAuthRefresh ||
    config.external ||
    !handlers
  ) {
    return null;
  }
  if (config.authRetried) {
    handlers.onAuthFailure();
    return null;
  }
  const token = await refreshOnce(handlers);
  if (!token) {
    handlers.onAuthFailure();
    return null;
  }
  config.authRetried = true;
  config.headers.set('Authorization', `Bearer ${token}`);
  return http.request(config);
}

http.interceptors.request.use(attachHeaders);
http.interceptors.response.use((response) => response, handleResponseError);
